import { AxiosError, AxiosResponse } from 'axios';
import apiClient from '../api/apiClient';
import type { Stylist, ServiceWithDetails } from '../types/stylist';

/**
 * Calculates the distance between two points (miles) via the Haversine formula.
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 3959 * c; // Earth radius in miles
}

/**
 * Human-readable distance label.
 */
function getDistanceLabel(stylist: Stylist, client: any): string | null {
  const sc = stylist.location.coordinates?.coordinates;
  const cc = client.location?.coordinates?.coordinates;
  if (!sc || !cc) return null;
  const dist = calculateDistance(cc[1], cc[0], sc[1], sc[0]);
  if (dist < 0.1) return 'Less than 0.1 miles away';
  if (dist < 1) return `${(dist * 10).toFixed(1)} blocks away`;
  if (dist < 10) return `${dist.toFixed(1)} miles away`;
  return `${Math.round(dist)} miles away`;
}

/**
 * Determines if stylist is within 10 miles or same ZIP/city.
 */
function isStylistNearby(stylist: Stylist, client: any): boolean {
  if (
    stylist.location.zipCode &&
    client.location.zipCode &&
    stylist.location.zipCode === client.location.zipCode
  ) {
    return true;
  }
  const sc = stylist.location.coordinates?.coordinates;
  const cc = client.location?.coordinates?.coordinates;
  if (sc && cc) {
    return calculateDistance(cc[1], cc[0], sc[1], sc[0]) <= 10;
  }
  return (
    stylist.location.city?.toLowerCase() === client.location.city?.toLowerCase()
  );
}

/**
 * Rank stylists by proximity and matching location fields.
 */
function rankStylistsByLocation(stylists: Stylist[], client: any): Stylist[] {
  return [...stylists].sort((a, b) => {
    const labelA = getDistanceLabel(a, client);
    const labelB = getDistanceLabel(b, client);
    if (labelA && labelB) {
      const numA = parseFloat(labelA);
      const numB = parseFloat(labelB);
      return numA - numB;
    }
    if (labelA) return -1;
    if (labelB) return 1;
    return 0;
  });
}

const stylistService = {
  // Fetch stylists with optional filters
  async getStylists(filters?: any): Promise<Stylist[]> {
    try {
      if (filters?.nearbyOnly && !filters.maxDistance) filters.maxDistance = 10;
      const res: AxiosResponse = await apiClient.get('/profile/stylists', {
        params: filters,
      });
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error('Get stylists error:', err.response?.data || err.message);
      throw err;
    }
  },

  // Stylists around coordinates
  async getNearbyStylists(
    latitude: number,
    longitude: number,
    maxDistance = 10,
    filters?: any
  ): Promise<Stylist[]> {
    try {
      const res: AxiosResponse = await apiClient.get(
        '/profile/stylists/nearby',
        {
          params: { ...filters, latitude, longitude, maxDistance },
        }
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error('Get nearby stylists error:', err.response?.data || err.message);
      throw err;
    }
  },

  processStylistsWithLocationData(
    stylists: Stylist[],
    client: any
  ): {
    sortedStylists: Stylist[];
    distanceLabels: Record<string, string | null>;
    nearbyStylistIds: Record<string, boolean>;
  } {
    const distanceLabels: Record<string, string | null> = {};
    const nearbyStylistIds: Record<string, boolean> = {};
    stylists.forEach((s) => {
      distanceLabels[s._id] = getDistanceLabel(s, client);
      nearbyStylistIds[s._id] = isStylistNearby(s, client);
    });
    return {
      sortedStylists: rankStylistsByLocation(stylists, client),
      distanceLabels,
      nearbyStylistIds,
    };
  },

  filterNearbyStylists(stylists: Stylist[], client: any): Stylist[] {
    return stylists.filter((s) => isStylistNearby(s, client));
  },

  async getStylist(id: string): Promise<Stylist> {
    try {
      const res: AxiosResponse = await apiClient.get(
        `/profile/stylists/${id}`
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error('Get stylist error:', err.response?.data || err.message);
      throw err;
    }
  },

  async getStylistProfile(): Promise<Stylist> {
    try {
      const res: AxiosResponse = await apiClient.get('/profile/stylist');
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
       'userQRSTUVWQRSTYUSER', err.response?.data || err.message
      );
      throw err;
    }
  },

  async updateStylistProfile(updates: Partial<Stylist>): Promise<Stylist> {
    try {
      const res: AxiosResponse = await apiClient.put(
        '/profile/stylist',
        updates
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Update stylist profile error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async updateCoverPhoto(formData: FormData): Promise<Stylist> {
    try {
      const res: AxiosResponse = await apiClient.post(
        '/profile/stylist/cover-photo',
        formData
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Update cover photo error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async deleteCoverPhoto(): Promise<Stylist> {
    try {
      const res: AxiosResponse = await apiClient.delete(
        '/profile/stylist/cover-photo'
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Delete cover photo error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async getStylistServices(stylistId: string, options?: { onlyEnabled?: boolean }): Promise<ServiceWithDetails[]> {
    try {
      const params = options?.onlyEnabled ? { isEnabled: 'true' } : undefined;
      const res: AxiosResponse = await apiClient.get(
        `/stylist-services/stylist/${stylistId}`,
        params ? { params } : undefined
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Get stylist services error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async updateStylistService(
    serviceId: string,
    data: Partial<ServiceWithDetails>
  ): Promise<ServiceWithDetails> {
    try {
      const res: AxiosResponse = await apiClient.put(
        `/stylist-services/${serviceId}`,{}
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Update stylist service error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async deleteStylistService(serviceId: string): Promise<void> {
    try {
      await apiClient.delete(`/stylist-services/${serviceId}`);
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Delete stylist service error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async searchStylists(query: string): Promise<Stylist[]> {
    try {
      const res: AxiosResponse = await apiClient.get(
        '/profile/stylists/search',
        { params: { query } }
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Search stylists error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  async filterByService(serviceName: string): Promise<Stylist[]> {
    try {
      const res: AxiosResponse = await apiClient.get('/profile/stylists', {
        params: { service: serviceName }
      });
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error(
        'Filter by service error:', err.response?.data || err.message
      );
      throw err;
    }
  },

  /**
   * Get favorite stylists and metadata.
   */
  async getFavoriteStylists(): Promise<{ stylists: Stylist[]; metadata: Record<string, any> }> {
    try {
      const res: AxiosResponse = await apiClient.get('/favorites');
      const favs = res.data.data;
      return {
        stylists: favs.map((f: any) => ({ ...f.stylist, isFavorite: true })),
        metadata: favs.reduce((acc: any, f: any) => ({
          ...acc,
          [f.stylist._id]: f.notes
        }), {})
      };
    } catch (error) {
      const err = error as AxiosError;
      console.error('Get favorite stylists error:', err.response?.data || err.message);
      throw err;
    }
  },

  async checkFavorite(stylistId: string): Promise<boolean> {
    try {
      const res: AxiosResponse = await apiClient.get(
        `/favorites/${stylistId}/check`
      );
      return res.data.data.isFavorited;
    } catch (error) {
      console.error('Check favorite error:', error);
      return false;
    }
  },

  async addFavorite(stylistId: string, notes?: string): Promise<void> {
    try {
      await apiClient.post(`/favorites/${stylistId}`, { notes });
    } catch (error) {
      const err = error as AxiosError;
      console.error('Add favorite error:', err.response?.data || err.message);
      throw err;
    }
  },

  async removeFavorite(stylistId: string): Promise<void> {
    try {
      await apiClient.delete(`/favorites/${stylistId}`);
    } catch (error) {
      const err = error as AxiosError;
      console.error('Remove favorite error:', err.response?.data || err.message);
      throw err;
    }
  },

  async updateFavoriteNotes(stylistId: string, notes: string): Promise<void> {
    try {
      await apiClient.put(`/favorites/${stylistId}`, { notes });
    } catch (error) {
      const err = error as AxiosError;
      console.error('Update favorite notes error:', err.response?.data || err.message);
      throw err;
    }
  },

  // Working hours methods
  async getWorkingHours(): Promise<any[]> {
    try {
      const res: AxiosResponse = await apiClient.get('/schedule/working-hours');
      return res.data.data || [];
    } catch (error) {
      const err = error as AxiosError;
      console.error('Get working hours error:', err.response?.data || err.message);
      throw err;
    }
  },

  async updateWorkingHours(workingHours: any[]): Promise<any> {
    try {
      const res: AxiosResponse = await apiClient.put(
        '/schedule/working-hours',
        { workingHours }
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError;
      console.error('Update working hours error:', err.response?.data || err.message);
      throw err;
    }
  }
};

export default stylistService;
